import { createStore } from "owa-satcheljs";
import type { MailboxInfo } from "owa-client-types";
import { getIndexerValueForMailboxInfo } from "owa-client-types";
import type { ElaborateFilterScenarios, ElaborateFilterSettings, AddinCopilotSettings, ExtensibilitySettingsResponse, LicenseType, UserType, ChatSettings, CopilotOnboardingStatus, } from "../services/types/serviceSettingTypes";
import registerDiagDataCollector from "../utils/registerDiagDataCollector";
import { getCopilotSettingsResponseFromStartupData } from "../services/helpers/getCopilotSettingsResponseFromStartupData";
import { createFeatureInfoMap } from "../services/helpers/createFeatureMapInfoFromResponse";
import { getOnboardingStatusFromResponse } from "../services/helpers/getOnboardingStatusFromResponse";
import type { InboxPrioritizationScenario } from "../services/types/serviceTypes";
import { onSaveGeneralPreferencesMutator } from "owa-mail-copilot-settings/lib/mutators/onSaveGeneralPreferencesMutator";
import onUpdatePrioritizeSettingsMutator from "owa-mail-copilot-settings/lib/mutators/onUpdatePrioritizeSettingsMutator";
import { Scenario } from "../services/types/serviceSettingTypes";

export type ImplementedCopilotFeatures = "Coach" | "Elaborate" | "Summarize" | "SuggestedDrafts" | "SuggestCategory" | "ExplainPlacesReport" | "Rewrite" | "ReplyByMeeting" | "MeetingPrepSummary" | "Chat" | "CoachApply" | "Inbox" | "TimeCopilotChat" | "VisualTheming" | "MChat" | "AgendaGeneration" | "AttachmentSummarization" | "AutoResourceBooking" | "BizChatHandoff" | "Memory" | "BuildDocumentContext" | "AttendeeColocation" | "RSVPSuggestions" | "ManagedEvents" | "PriorityCalendar" | "Prioritize" | "CalendarInstructions" | "CalendarManageEventInstructions" | "CalendarAutoRoomBookingInstructions" | "MailCoauthoring" | "DailyBriefing" | "ProactiveDrafts" | "SuggestedUserActions" | "PersonalOS" | "EmailPersonalization" | "ThreadSummarizationSydney";

export enum FetchState {
    NOT_STARTED,
    STARTUPCACHE,
    IN_PROGRESS,
    SUCCEEDED,
    FAILED
}

export interface FeatureInfo {
    isEnabled: true;
    langauges: string[];
    capabilities?: CopilotFeatureCapabilities;
    optInEnabled?: boolean;
}

export type CopilotFeatureCapabilities = ElaborateFeatureCapabilities | RewriteFeatureCapabilities | TimeCopilotChatFeatureCapabilities | ThreadSummarizationSydneyFeatureCapabilities | InboxPrioritizationFeatureCapabilities;

export interface ElaborateFeatureCapabilities {
    tonePersonalization: boolean;
    calendarElaborate: boolean;
    customStyleInstructions: boolean;
    sydneyCapable: boolean;
}

export interface RewriteFeatureCapabilities {
    highlightAndRewrite: boolean;
    calendarRewrite: boolean;
    autoRewriteStaticPrompt: boolean;
    customStyleInstructions: boolean;
    personalizedStaticPrompt: boolean;
}

export interface TimeCopilotChatFeatureCapabilities {
    triage: boolean;
    schedulingCompactCard: boolean;
    workHoursLocation: boolean;
    speedyMeetings: boolean;
    darkMode: boolean;
    categorizationUpdates: boolean;
    calTimeSuggestionsV2: boolean;
    monCopilotChatContextIq: boolean;
}

export interface ThreadSummarizationSydneyFeatureCapabilities {
    threadSummarizationSydney: true;
}

export interface InboxPrioritizationFeatureCapabilities {
    backgroundProcessing: boolean;
}

export interface CopilotCapableAddinInfo {
    titleId: string;
    manifestId?: string;
    isOldModelAddin?: boolean;
    acquisitionDate?: string;
    isAdminInstalled: boolean;
    scenarioStrings: Map<ImplementedCopilotFeatures, AddinScenarioStrings>;
    menuIcon?: string;
    fallbackAddinName: string;
    copilotSettings?: AddinCopilotSettings;
}

export interface AddinScenarioStrings {
    menuLabelText?: string;
    menuSelectedText?: string;
    launchButtonText?: string;
}

export interface AddinSettings {
    tenantDefault?: string;
    lastUsedAddin?: string;
}

export type ScenarioToAddinSettingsMap = {
    [key in ImplementedCopilotFeatures]?: AddinSettings;
};

export type FeatureMap = Map<ImplementedCopilotFeatures, FeatureInfo>;

export type AddinsByScenario = Map<ImplementedCopilotFeatures, CopilotCapableAddinInfo[]>;
export type AddinInfoById = Map<string, CopilotCapableAddinInfo>;
interface Extensibility {
    addinsInfo: AddinsByScenario;
    addinInfoById: AddinInfoById;
    scenarioToAddinSettingsMap: ScenarioToAddinSettingsMap;
    settingsFromService?: ExtensibilitySettingsResponse;
}

export interface CopilotSettingsStore {
    isEnabled: boolean;
    featureInfo: FeatureMap;
    extensibility: Extensibility;
    licenses: LicenseType[];
    inheritedLicenses: LicenseType[];
    fetchState: FetchState;
    isBetaUser: boolean;
    isFrontierPublicUser: boolean;
    isFrontierPrivateTAPUser: boolean;
    isFrontierStagingUser: boolean;
    isFrontierInfluencerUser: boolean;
    userTypes: UserType[];
    acquisitionsSettings?: AcquisitionsSettings;
    copilotAddinsPromise: Promise<void>;
    copilotAddinsPromiseResolve?: () => void;
    chatSettings?: ChatSettings;
    // Onboarding status passed through from the PersonalOS service on the
    // PersonalOS scenario. Absent when the server did not send it; `Unknown` when
    // OCS could not determine it.
    personalOSOnboardingStatus?: CopilotOnboardingStatus;
}

export type AcquisitionsSettings = {
    enableCopilotExtensibility: boolean;
    promptUserToAcquireCopilot: boolean;
};

interface CopilotSettingsMetaStore {
    indexerToStoreMap: Map<string, CopilotSettingsStore>;
}

const CopilotSettingsMetaStore = createStore<CopilotSettingsMetaStore>("CopilotSettingsMetaStore", {
    indexerToStoreMap: new Map(),
});

export interface ElaborateSettingsCache {
    elaborateFilterOptions: Map<ElaborateFilterScenarios, ElaborateFilterSettings>;
    tonePersonalizationSeen: boolean;
}

export default function copilotSettingsStore(mailboxInfo: MailboxInfo): CopilotSettingsStore {
    const name = getIndexerValueForMailboxInfo(mailboxInfo) + "_copilotSettingsStore";
    let cachedStore = CopilotSettingsMetaStore().indexerToStoreMap.get(name);
    if (!cachedStore) {
        let copilotAddinsPromiseResolve;
        let storeObj: CopilotSettingsStore;
        const cachedStoreResponse = getCopilotSettingsResponseFromStartupData(mailboxInfo);
        
        if (cachedStoreResponse) {
            storeObj = {
                isEnabled: cachedStoreResponse.copilotCapable.capable,
                licenses: cachedStoreResponse.copilotLicenses,
                inheritedLicenses: cachedStoreResponse.inheritedCopilotLicenses,
                featureInfo: createFeatureInfoMap(cachedStoreResponse.supportedScenarios),
                extensibility: {
                    addinsInfo: new Map(),
                    addinInfoById: new Map(),
                    scenarioToAddinSettingsMap: {},
                    settingsFromService: cachedStoreResponse.extensibility,
                },
                fetchState: FetchState.STARTUPCACHE,
                isBetaUser: cachedStoreResponse.isBetaUser,
                isFrontierPublicUser: !!cachedStoreResponse.isFrontierPublicUser,
                isFrontierPrivateTAPUser: !!cachedStoreResponse.isFrontierPrivateTAPUser,
                isFrontierStagingUser: !!cachedStoreResponse.isFrontierPrivateStagingUser,
                isFrontierInfluencerUser: !!cachedStoreResponse.isFrontierPrivateInfluencersUser,
                userTypes: cachedStoreResponse.userTypes,
                chatSettings: cachedStoreResponse.chatSettings,
                personalOSOnboardingStatus: getOnboardingStatusFromResponse(cachedStoreResponse),
                copilotAddinsPromise: new Promise(resolve => {
                    copilotAddinsPromiseResolve = resolve;
                }),
            };
            
            // Make sure we do this in a separate microtask as this will call a mutator
            // and this getter should not call mutators to avoid mutators being called
            // within mutators
            Promise.resolve().then(() => {
                onSaveGeneralPreferencesMutator(mailboxInfo, cachedStoreResponse.userPreferences?.generalPreferences);
                
                const inboxCatchupScenario = cachedStoreResponse.supportedScenarios.find(scenario => scenario.scenario === Scenario.InboxCatchup) as InboxPrioritizationScenario | undefined;
                if (inboxCatchupScenario) {
                    const preferences = inboxCatchupScenario.userPreferences;
                    const isOptInEnabled = inboxCatchupScenario.optInSettings?.enabled ?? false;
                    
                    if (preferences) {
                        onUpdatePrioritizeSettingsMutator(mailboxInfo, {
                            // When opt-in is enabled, ignore server's prioritizationEnabled and
                            // start with false. User must opt in via the toggle.
                            prioritizationEnabled: isOptInEnabled
                                ? false
                                : (preferences.prioritizationEnabled ?? false),
                            applyLowPriorityLabel: preferences.applyLowPriorityLabel ?? false,
                            replacePreviewWithMicroSummary: preferences.replacePreviewWithMicroSummary ?? false,
                        });
                    }
                }
            });
        }
        else {
            storeObj = {
                isEnabled: false,
                licenses: [],
                inheritedLicenses: [],
                featureInfo: new Map(),
                extensibility: {
                    addinsInfo: new Map(),
                    addinInfoById: new Map(),
                    scenarioToAddinSettingsMap: {},
                },
                fetchState: FetchState.NOT_STARTED,
                isBetaUser: false,
                isFrontierPrivateTAPUser: false,
                isFrontierPublicUser: false,
                isFrontierStagingUser: false,
                isFrontierInfluencerUser: false,
                userTypes: [],
                chatSettings: undefined,
                personalOSOnboardingStatus: undefined,
                copilotAddinsPromise: new Promise(resolve => {
                    copilotAddinsPromiseResolve = resolve;
                }),
            };
        }
        
        storeObj.copilotAddinsPromiseResolve = copilotAddinsPromiseResolve;
        CopilotSettingsMetaStore().indexerToStoreMap.set(name, storeObj);
        cachedStore = CopilotSettingsMetaStore().indexerToStoreMap.get(name) ?? storeObj;
    }
    
    return cachedStore;
}

export function getAllStoreDataForDiagnostics(): string[] {
    const returnArray: string[] = [];
    for (const entry of CopilotSettingsMetaStore().indexerToStoreMap.entries()) {
        const [key, storeObj] = entry;
        returnArray.push(JSON.stringify({
            storeIndexer: key,
            store: storeObj,
        }));
    }
    return returnArray;
}

/* eslint-disable-next-line owa-custom-rules/no-side-effects -- (https://aka.ms/OWALintWiki)
 * DO NOT COPY-PASTE!, this function can't be called in the module scope, please move it inside a function
 *	> Function registerDiagDataCollector called at module scope is not allowed. This may cause unwanted side effects. */
registerDiagDataCollector();
